// API client (replaces Firebase). Keeps the SAME exported function names the
// rest of the app imports, but every call now hits the self-hosted backend.
import { apiGet, apiPost, apiDelete, apiUpload, setToken } from "./api";

// ---------------------------------------------------------------------------
// Lightweight auth (replaces Firebase Auth). JWT stored via api.setToken; the
// admin user + minimal {uid,email} kept here and mirrored to localStorage.
// ---------------------------------------------------------------------------
const USER_KEY = "adminUserBasic";
let currentUser = null;
try {
  const raw = localStorage.getItem(USER_KEY);
  if (raw) currentUser = JSON.parse(raw);
} catch { /* ignore */ }

const authListeners = new Set();
const notify = () => authListeners.forEach((cb) => {
  try { cb(currentUser); } catch (e) { console.error(e); }
});

function setCurrentUser(user) {
  currentUser = user;
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
  notify();
}

export const getCurrentUser = () => currentUser;

export const onAuthStateChange = (callback) => {
  authListeners.add(callback);
  // Emit current state asynchronously, mirroring Firebase behaviour.
  Promise.resolve().then(() => callback(currentUser));
  return () => authListeners.delete(callback);
};

export const loginAdmin = async (email, password) => {
  const result = await apiPost("/admin/login", { email, password });
  setToken(result.token);
  const user = result.user || { uid: result.adminUser?.uid, email };
  // stash adminUser so getAdminUserData can return it without a round-trip
  lastAdminUser = result.adminUser || null;
  setCurrentUser(user);
  return user;
};

export const logoutAdmin = async () => {
  setToken(null);
  lastAdminUser = null;
  setCurrentUser(null);
};

let lastAdminUser = null;

// ---------------------------------------------------------------------------
// Admin user data / roles / 2FA
// ---------------------------------------------------------------------------
export const getAdminUserData = async (uid) => {
  if (lastAdminUser && (lastAdminUser.uid === uid || lastAdminUser.id === uid)) {
    return lastAdminUser;
  }
  const res = await apiGet(`/admin/user/${encodeURIComponent(uid)}`);
  lastAdminUser = res.adminUser;
  return res.adminUser;
};

export const updateAdminUserData = async (uid, updateData) => {
  // Only role updates are currently needed through this path.
  if (updateData && updateData.role) {
    await apiPost("/admin/users/role", { userId: uid, role: updateData.role });
  }
  return { success: true };
};

export const enable2FAForUser = async (uid, secretData, backupCodes) =>
  apiPost("/admin/2fa/enable", { uid, secret: secretData, backupCodes });

export const disable2FAForUser = async (uid) =>
  apiPost("/admin/2fa/disable", { uid });

export const updateLoginStats = async () => {
  // Login stats are updated server-side on /admin/login. No-op here.
  return { success: true };
};

export const markBackupCodeAsUsed = async (uid, usedCode) => {
  const res = await apiPost("/admin/2fa/use-backup", { uid, code: usedCode });
  return res.remaining;
};

export const updateUserRole = async (userId, newRole) =>
  apiPost("/admin/users/role", { userId, role: newRole });

export const getAllAdminUsers = async () => (await apiGet("/admin/users")).users;

export const getUserRole = async (userId) => {
  try {
    const res = await apiGet(`/admin/user/${encodeURIComponent(userId)}`);
    return res.adminUser?.role || "marketing";
  } catch {
    return "marketing";
  }
};

// ---------------------------------------------------------------------------
// Users / appointments
// ---------------------------------------------------------------------------
export const saveUserInfo = async (userData) => {
  const res = await apiPost("/saveUser", userData);
  return res.userId;
};

export const saveAppointment = async (userId, appointmentData) => {
  const res = await apiPost("/saveAppointment", { userId, appointmentData });
  return { id: res.appointmentId, ogrenciNumarasi: res.ogrenciNumarasi, meetLink: res.meetLink };
};

export const getBookedSlots = async (date) => {
  const d = date instanceof Date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : date;
  return (await apiGet("/getBookedSlots", { date: d })).bookedSlots || [];
};

export const getAllAppointments = async () =>
  (await apiGet("/getAllAppointments")).appointments || [];

export const updateAppointmentStatus = async (appointmentId, newStatus) =>
  apiPost("/updateAppointmentStatus", { appointmentId, status: newStatus });

export const deleteAppointment = async (appointmentId) =>
  apiDelete("/deleteAppointment", { appointmentId });

// ---------------------------------------------------------------------------
// Availability / blocked dates / FOMO settings
// ---------------------------------------------------------------------------
export const getAvailabilitySettings = async () =>
  (await apiGet("/getAvailabilitySettings")).settings;

export const saveAvailabilitySettings = async (settings) =>
  apiPost("/saveAvailabilitySettings", settings);

export const getBlockedDates = async () =>
  (await apiGet("/getBlockedDates")).blockedDates || [];

export const addBlockedDate = async (dateData) =>
  (await apiPost("/addBlockedDate", dateData)).id;

export const removeBlockedDate = async (dateId) =>
  apiDelete("/removeBlockedDate", { dateId });

export const getFomoSettings = async () =>
  (await apiGet("/getFomoSettings")).settings;

export const saveFomoSettings = async (settings) =>
  apiPost("/saveFomoSettings", settings);

export const getAppointmentCount = async () => {
  try {
    return (await apiGet("/getAppointmentCount")).count;
  } catch {
    const today = new Date().toISOString().split("T")[0];
    const seed = today.split("-").reduce((a, p) => a + parseInt(p), 0);
    return 30 + (seed % 21);
  }
};

// ---------------------------------------------------------------------------
// Students
// ---------------------------------------------------------------------------
export const generateStudentNumber = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 8; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
  return out;
};

export const generateQueryCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let out = "";
  for (let i = 0; i < 8; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
  return out;
};

export const getAllStudents = async () =>
  (await apiGet("/getAllStudents")).students || [];

export const addStudent = async (studentData) =>
  (await apiPost("/addStudent", studentData)).student;

export const updateStudent = async (studentId, studentData) =>
  apiPost("/updateStudent", { studentId, studentData });

export const deleteStudent = async (studentId) =>
  apiDelete("/deleteStudent", { studentId });

export const ensureQueryCode = async (studentId) =>
  (await apiPost("/ensureQueryCode", { studentId })).code;

export const findStudentByQueryCode = async (queryCode) => {
  if (!queryCode || queryCode.trim().length !== 8) throw new Error("Geçersiz sorgula kodu formatı");
  return (await apiGet("/findStudentByQueryCode", { code: queryCode.trim().toUpperCase() })).student;
};

export const findStudentOrAppointmentByNumber = async (studentNumber) => {
  if (!studentNumber || studentNumber.trim().length !== 8) throw new Error("Geçersiz öğrenci numarası formatı");
  return (await apiGet("/findStudentOrAppointmentByNumber", { number: studentNumber.trim().toUpperCase() })).result;
};

export const findAppointmentByStudentNumber = async (studentNumber) => {
  const result = await findStudentOrAppointmentByNumber(studentNumber);
  return result && result.type === "appointment" ? result : null;
};

// ---------------------------------------------------------------------------
// Mailing list
// ---------------------------------------------------------------------------
export const getMailingList = async () =>
  (await apiGet("/getMailingList")).emails || [];

export const addEmailToMailingList = async (email) =>
  apiPost("/addEmailToMailingList", { email });

export const removeEmailFromMailingList = async (emailId) =>
  apiDelete("/removeEmailFromMailingList", { emailId });

export const updateEmailStatus = async (emailId, status) =>
  apiPost("/updateEmailStatus", { emailId, status });

export const getMailingListStats = async () => {
  try { return await apiGet("/getMailingListStats"); }
  catch { return { total: 0, active: 0, unsubscribed: 0 }; }
};

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
export const saveAnalyticsSession = async (sessionData) =>
  (await apiPost("/saveAnalyticsSession", sessionData)).id;

export const getAnalyticsData = async (limit = 100) =>
  (await apiGet("/getAnalyticsData", { limit })).analytics || [];

export const getTrafficSourceStats = async (dateRange = 30) =>
  (await apiGet("/getTrafficSourceStats", { dateRange })).stats || {};

export const clearAnalyticsData = async () => {
  await apiDelete("/clearAnalyticsData");
  return true;
};

// ---------------------------------------------------------------------------
// Admin activity logs
// ---------------------------------------------------------------------------
export const logAdminActivity = async (action, details = {}) => {
  try {
    const user = getCurrentUser();
    return await apiPost("/logAdminActivity", {
      adminEmail: user?.email,
      adminUid: user?.uid,
      action,
      details,
      ipAddress: "client-side",
      userAgent: navigator.userAgent,
    });
  } catch (e) {
    console.error("Error logging admin activity:", e);
  }
};

export const getAdminActivityLogs = async (filters = {}) =>
  apiGet("/getAdminActivityLogs", filters);

export const getAdminActivityStats = async (days = 30) =>
  apiGet("/getAdminActivityStats", { days });

// ---------------------------------------------------------------------------
// Document uploads (multipart to backend local-disk storage)
// ---------------------------------------------------------------------------
export const uploadDocument = async (ogrenciNumarasi, category, fileName, file) => {
  const fd = new FormData();
  fd.append("ogrenciNumarasi", ogrenciNumarasi);
  fd.append("category", category);
  fd.append("fileName", fileName);
  fd.append("file", file);
  return apiUpload("/uploadDocument", fd);
};

export const getStudentDocuments = async (ogrenciNumarasi) =>
  (await apiGet("/getStudentDocuments", { ogrenciNumarasi })).documents;

export const deleteDocument = async (filePath) =>
  apiDelete("/deleteDocument", { filePath });
